import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import InboundLeadFormModal from "../../AdminPanel/tabs/CRM/modals/InboundLeadFormModal";
import ImportLeadsModal from "../../AdminPanel/tabs/CRM/modals/ImportLeadsModal";
import LeadStatusBadge from "../../AdminPanel/tabs/CRM/shared/LeadStatusBadge";
import { CITIES_BY_STATE } from "../../AdminPanel/tabs/CRM/shared/usCities";

const BASE = "/admin/crm/inbound/leads";

const LEAD_TYPES = [
    { value: "",               label: "All Types" },
    { value: "retailer_brand", label: "Retailer / Brand" },
    { value: "grower",         label: "Grower" },
    { value: "consumer",       label: "Consumer" },
];

const US_STATES = [
    { abbr: "AL", name: "Alabama" }, { abbr: "AK", name: "Alaska" },
    { abbr: "AZ", name: "Arizona" }, { abbr: "AR", name: "Arkansas" },
    { abbr: "CA", name: "California" }, { abbr: "CO", name: "Colorado" },
    { abbr: "CT", name: "Connecticut" }, { abbr: "DE", name: "Delaware" },
    { abbr: "FL", name: "Florida" }, { abbr: "GA", name: "Georgia" },
    { abbr: "HI", name: "Hawaii" }, { abbr: "ID", name: "Idaho" },
    { abbr: "IL", name: "Illinois" }, { abbr: "IN", name: "Indiana" },
    { abbr: "IA", name: "Iowa" }, { abbr: "KS", name: "Kansas" },
    { abbr: "KY", name: "Kentucky" }, { abbr: "LA", name: "Louisiana" },
    { abbr: "ME", name: "Maine" }, { abbr: "MD", name: "Maryland" },
    { abbr: "MA", name: "Massachusetts" }, { abbr: "MI", name: "Michigan" },
    { abbr: "MN", name: "Minnesota" }, { abbr: "MS", name: "Mississippi" },
    { abbr: "MO", name: "Missouri" }, { abbr: "MT", name: "Montana" },
    { abbr: "NE", name: "Nebraska" }, { abbr: "NV", name: "Nevada" },
    { abbr: "NH", name: "New Hampshire" }, { abbr: "NJ", name: "New Jersey" },
    { abbr: "NM", name: "New Mexico" }, { abbr: "NY", name: "New York" },
    { abbr: "NC", name: "North Carolina" }, { abbr: "ND", name: "North Dakota" },
    { abbr: "OH", name: "Ohio" }, { abbr: "OK", name: "Oklahoma" },
    { abbr: "OR", name: "Oregon" }, { abbr: "PA", name: "Pennsylvania" },
    { abbr: "RI", name: "Rhode Island" }, { abbr: "SC", name: "South Carolina" },
    { abbr: "SD", name: "South Dakota" }, { abbr: "TN", name: "Tennessee" },
    { abbr: "TX", name: "Texas" }, { abbr: "UT", name: "Utah" },
    { abbr: "VT", name: "Vermont" }, { abbr: "VA", name: "Virginia" },
    { abbr: "WA", name: "Washington" }, { abbr: "WV", name: "West Virginia" },
    { abbr: "WI", name: "Wisconsin" }, { abbr: "WY", name: "Wyoming" },
    { abbr: "DC", name: "District of Columbia" },
];

const LIMIT = 25;

const InboundLeadList = ({ onSelectLead, defaultFilters }) => {
    const [leads, setLeads]           = useState([]);
    const [total, setTotal]           = useState(0);
    const [loading, setLoading]       = useState(false);
    const [page, setPage]             = useState(1);
    const [filters, setFilters]       = useState(
        defaultFilters
            ? { firstName: "", lastName: "", email: "", state: "", city: "", leadType: "", ...defaultFilters }
            : { firstName: "", lastName: "", email: "", state: "", city: "", leadType: "" }
    );
    const [sort, setSort]             = useState("createdAt");
    const [order, setOrder]           = useState("desc");
    const [showForm, setShowForm]     = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [editLead, setEditLead]     = useState(null);
    const [copiedId, setCopiedId]     = useState(null);
    const [confirmDelete, setConfirmDelete]         = useState(null);
    const [deleting, setDeleting]                   = useState(false);
    const [selectedIds, setSelectedIds]             = useState(new Set());
    const [selectAllFiltered, setSelectAllFiltered] = useState(false);
    const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
    const [bulkDeleting, setBulkDeleting]           = useState(false);
    const [showBulkEdit, setShowBulkEdit]           = useState(false);
    const [bulkStatus, setBulkStatus]               = useState("");
    const [bulkPriority, setBulkPriority]           = useState("");
    const [bulkUpdating, setBulkUpdating]           = useState(false);

    const availableCities = filters.state ? (CITIES_BY_STATE[filters.state] || []) : [];

    const fetchLeads = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: p, limit: LIMIT, sort, order });
            Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
            const res = await apiClient.get(`${BASE}?${params}`);
            setLeads(res.data.leads || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [filters, sort, order]);

    useEffect(() => {
        setPage(1);
        setSelectedIds(new Set());
        setSelectAllFiltered(false);
        fetchLeads(1);
    }, [filters, sort, order]);

    const handleSort = (col) => {
        if (sort === col) setOrder(o => o === "asc" ? "desc" : "asc");
        else { setSort(col); setOrder("asc"); }
    };
    const sortArrow = (col) => sort === col ? (order === "asc" ? " ▲" : " ▼") : "";

    const copyToClipboard = (text, key) => {
        navigator.clipboard.writeText(text);
        setCopiedId(key);
        setTimeout(() => setCopiedId(c => c === key ? null : c), 2000);
    };

    // ── Selection helpers ────────────────────────────────────────────────────
    const pageIds = leads.map(l => l.id);
    const allPageSelected  = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
    const somePageSelected = pageIds.some(id => selectedIds.has(id));
    const selectionActive  = selectedIds.size > 0 || selectAllFiltered;
    const selectionCount   = selectAllFiltered ? total : selectedIds.size;

    const handleToggleRow = (id) => {
        if (selectAllFiltered) {
            const next = new Set(pageIds); next.delete(id);
            setSelectedIds(next); setSelectAllFiltered(false); return;
        }
        setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    };

    const handleToggleAllPage = () => {
        if (selectAllFiltered) { setSelectAllFiltered(false); setSelectedIds(new Set()); return; }
        if (allPageSelected) {
            setSelectedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.delete(id)); return n; });
        } else {
            setSelectedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.add(id)); return n; });
        }
    };

    const handleClearSelection = () => { setSelectAllFiltered(false); setSelectedIds(new Set()); };

    // ── Delete ───────────────────────────────────────────────────────────────
    const handleDeleteLead = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await apiClient.delete(`${BASE}/${confirmDelete.id}`);
            setConfirmDelete(null);
            fetchLeads(page);
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete lead");
        } finally {
            setDeleting(false);
        }
    };

    const handleBulkDelete = async () => {
        setBulkDeleting(true);
        try {
            const payload = selectAllFiltered
                ? { filters: { ...filters } }
                : { leadIds: [...selectedIds] };
            await apiClient.delete(`${BASE}/bulk`, { data: payload });
            setBulkConfirmDelete(false);
            handleClearSelection();
            setPage(1);
            fetchLeads(1);
        } catch (err) {
            alert(err.response?.data?.error || "Bulk delete failed");
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleBulkEdit = async () => {
        if (!bulkStatus && !bulkPriority) return;
        setBulkUpdating(true);
        try {
            const payload = selectAllFiltered
                ? { filters: { ...filters }, ...(bulkStatus && { status: bulkStatus }), ...(bulkPriority && { priority: bulkPriority }) }
                : { leadIds: [...selectedIds], ...(bulkStatus && { status: bulkStatus }), ...(bulkPriority && { priority: bulkPriority }) };
            await apiClient.patch(`${BASE}/bulk`, payload);
            setShowBulkEdit(false);
            setBulkStatus("");
            setBulkPriority("");
            handleClearSelection();
            fetchLeads(page);
        } catch (err) {
            alert(err.response?.data?.error || "Bulk update failed");
        } finally {
            setBulkUpdating(false);
        }
    };

    const totalPages = Math.ceil(total / LIMIT);
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : "—";

    return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>

            {/* Filter bar */}
            <div className="crm-filter-bar" style={{ flexWrap: "wrap", gap: "var(--space-2)", flexShrink: 0 }}>
                <input
                    className="admin-search-input"
                    style={{ minWidth: 110, flex: "1 1 110px" }}
                    placeholder="First name…"
                    value={filters.firstName}
                    onChange={e => setFilters(f => ({ ...f, firstName: e.target.value }))}
                />
                <input
                    className="admin-search-input"
                    style={{ minWidth: 110, flex: "1 1 110px" }}
                    placeholder="Last name…"
                    value={filters.lastName}
                    onChange={e => setFilters(f => ({ ...f, lastName: e.target.value }))}
                />
                <input
                    className="admin-search-input"
                    style={{ minWidth: 150, flex: "1 1 150px" }}
                    placeholder="Email…"
                    value={filters.email}
                    onChange={e => setFilters(f => ({ ...f, email: e.target.value }))}
                />
                <select
                    value={filters.state}
                    onChange={e => setFilters(f => ({ ...f, state: e.target.value, city: "" }))}
                >
                    <option value="">All States</option>
                    {US_STATES.map(s => <option key={s.abbr} value={s.abbr}>{s.name}</option>)}
                </select>
                <select
                    value={filters.city}
                    onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                    disabled={!filters.state}
                >
                    <option value="">All Cities</option>
                    {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filters.leadType} onChange={e => setFilters(f => ({ ...f, leadType: e.target.value }))}>
                    {LEAD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <span className="admin-search-count">{total} lead{total !== 1 ? "s" : ""}</span>
                <div style={{ marginLeft: "auto", display: "flex", gap: "var(--space-2)", flexShrink: 0 }}>
                    <button className="admin-btn" onClick={() => setShowImport(true)}>↑ Import CSV</button>
                    <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(true)}>+ New Lead</button>
                </div>
            </div>

            {/* Select-all banner */}
            {allPageSelected && !selectAllFiltered && total > LIMIT && (
                <div className="crm-select-all-banner" style={{ flexShrink: 0 }}>
                    All {leads.length} leads on this page are selected.{" "}
                    <button className="crm-select-all-banner__action" onClick={() => { setSelectAllFiltered(true); setSelectedIds(new Set()); }}>
                        Select all {total} leads matching current filters
                    </button>
                </div>
            )}
            {selectAllFiltered && (
                <div className="crm-select-all-banner crm-select-all-banner--active" style={{ flexShrink: 0 }}>
                    All {total} leads matching current filters are selected.{" "}
                    <button className="crm-select-all-banner__action" onClick={handleClearSelection}>Clear selection</button>
                </div>
            )}

            {/* Bulk action bar */}
            {selectionActive && (
                <div className="crm-bulk-bar" style={{ flexShrink: 0, flexWrap: "wrap", gap: "var(--space-2)" }}>
                    <span className="crm-bulk-bar__count">{selectionCount} lead{selectionCount !== 1 ? "s" : ""} selected</span>
                    <div className="crm-bulk-bar__actions" style={{ flexWrap: "wrap", gap: "var(--space-2)" }}>
                        <button
                            className={`admin-btn${showBulkEdit ? " admin-btn--active" : ""}`}
                            onClick={() => { setShowBulkEdit(v => !v); setBulkStatus(""); setBulkPriority(""); }}
                        >
                            ✏ Edit Status / Priority
                        </button>
                        <button className="admin-btn admin-btn--danger" onClick={() => setBulkConfirmDelete(true)}>🗑 Delete Selected</button>
                        <button className="admin-btn" onClick={() => { handleClearSelection(); setShowBulkEdit(false); }}>Cancel</button>
                    </div>
                    {showBulkEdit && (
                        <div className="crm-bulk-edit-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap", paddingTop: 6 }}>
                            <select
                                value={bulkStatus}
                                onChange={e => setBulkStatus(e.target.value)}
                                style={{ minWidth: 150 }}
                            >
                                <option value="">— Status (no change) —</option>
                                <option value="new">New</option>
                                <option value="active">Active</option>
                                <option value="won">Won</option>
                                <option value="lost">Lost</option>
                                <option value="converted">Converted</option>
                            </select>
                            <select
                                value={bulkPriority}
                                onChange={e => setBulkPriority(e.target.value)}
                                style={{ minWidth: 160 }}
                            >
                                <option value="">— Priority (no change) —</option>
                                <option value="hot">Hot</option>
                                <option value="warm">Warm</option>
                                <option value="cold">Cold</option>
                            </select>
                            <button
                                className="admin-btn admin-btn--primary"
                                disabled={(!bulkStatus && !bulkPriority) || bulkUpdating}
                                onClick={handleBulkEdit}
                            >
                                {bulkUpdating ? "Applying…" : `Apply to ${selectionCount} lead${selectionCount !== 1 ? "s" : ""}`}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Table area — scrolls */}
            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : leads.length === 0 ? (
                <p className="crm-empty">No inbound leads found.</p>
            ) : (
                <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                    <table className="crm-lead-table">
                        <thead>
                            <tr>
                                <th style={{ width: 36 }} onClick={e => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        className="crm-checkbox"
                                        checked={allPageSelected || selectAllFiltered}
                                        ref={el => { if (el) el.indeterminate = !selectAllFiltered && !allPageSelected && somePageSelected; }}
                                        onChange={handleToggleAllPage}
                                    />
                                </th>
                                <th onClick={() => handleSort("firstName")}>Name{sortArrow("firstName")}</th>
                                <th>Type</th>
                                <th onClick={() => handleSort("state")}>State{sortArrow("state")}</th>
                                <th onClick={() => handleSort("city")}>City{sortArrow("city")}</th>
                                <th>Status</th>
                                <th>Priority</th>
                                <th onClick={() => handleSort("createdAt")}>Created{sortArrow("createdAt")}</th>
                                <th style={{ width: 80 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map(lead => (
                                <tr key={lead.id}>
                                    <td style={{ width: 36 }} onClick={e => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="crm-checkbox"
                                            checked={selectAllFiltered || selectedIds.has(lead.id)}
                                            onChange={() => handleToggleRow(lead.id)}
                                        />
                                    </td>
                                    <td>
                                        <span className="crm-lead-name-link" onClick={() => onSelectLead(lead.id)}>
                                            {lead.firstName} {lead.lastName}
                                        </span>
                                        <br />
                                        <small style={{ color: "var(--color-text-secondary)" }}>
                                            {lead.email}
                                            <button
                                                className="crm-copy-inline-btn"
                                                title="Copy email"
                                                onClick={e => { e.stopPropagation(); copyToClipboard(lead.email, `email-${lead.id}`); }}
                                            >
                                                {copiedId === `email-${lead.id}` ? "✓" : "⎘"}
                                            </button>
                                        </small>
                                    </td>
                                    <td>
                                        {lead.leadType
                                            ? LEAD_TYPES.find(t => t.value === lead.leadType)?.label || lead.leadType
                                            : <span style={{ color: "var(--color-text-secondary)" }}>—</span>}
                                    </td>
                                    <td>{lead.state || <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</td>
                                    <td>{lead.city  || <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</td>
                                    <td><LeadStatusBadge status={lead.status} /></td>
                                    <td>
                                        <span className={`crm-priority-badge crm-priority-badge--${lead.priority}`}>
                                            {lead.priority}
                                        </span>
                                    </td>
                                    <td>{fmtDate(lead.createdAt)}</td>
                                    <td style={{ width: 80 }} onClick={e => e.stopPropagation()}>
                                        <button
                                            className="crm-row-edit-btn"
                                            onClick={e => { e.stopPropagation(); setEditLead(lead); }}
                                        >
                                            ✏ Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="admin-pagination">
                            <button className="admin-page-btn" disabled={page === 1} onClick={() => { setPage(page - 1); fetchLeads(page - 1); }}>← Prev</button>
                            <span className="admin-page-info">Page {page} of {totalPages}</span>
                            <button className="admin-page-btn" disabled={page === totalPages} onClick={() => { setPage(page + 1); fetchLeads(page + 1); }}>Next →</button>
                        </div>
                    )}
                </div>
            )}

            {/* Import CSV */}
            {showImport && (
                <ImportLeadsModal
                    endpoint="/admin/crm/inbound/leads/import"
                    onClose={() => setShowImport(false)}
                    onImported={() => { setPage(1); fetchLeads(1); }}
                />
            )}

            {/* New lead */}
            {showForm && (
                <InboundLeadFormModal
                    baseEndpoint={BASE}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); fetchLeads(page); }}
                />
            )}

            {/* Edit lead */}
            {editLead && (
                <InboundLeadFormModal
                    lead={editLead}
                    baseEndpoint={BASE}
                    onClose={() => setEditLead(null)}
                    onSaved={() => { setEditLead(null); fetchLeads(page); }}
                />
            )}

            {/* Single delete confirmation */}
            {confirmDelete && (
                <div className="crm-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
                    <div className="crm-modal" style={{ maxWidth: 440 }}>
                        <div className="crm-modal__header">
                            <h2 className="crm-modal__title">Delete Lead</h2>
                            <button className="crm-modal__close" onClick={() => setConfirmDelete(null)}>×</button>
                        </div>
                        <div className="crm-modal__body">
                            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                                Permanently delete <strong>{confirmDelete.firstName} {confirmDelete.lastName}</strong>? <strong>This cannot be undone.</strong>
                            </p>
                        </div>
                        <div className="crm-modal__footer">
                            <button className="admin-btn" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</button>
                            <button className="admin-btn admin-btn--danger" onClick={handleDeleteLead} disabled={deleting}>
                                {deleting ? "Deleting…" : "Delete Permanently"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk delete confirmation */}
            {bulkConfirmDelete && (
                <div className="crm-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setBulkConfirmDelete(false); }}>
                    <div className="crm-modal" style={{ maxWidth: 440 }}>
                        <div className="crm-modal__header">
                            <h2 className="crm-modal__title">Delete {selectionCount} Lead{selectionCount !== 1 ? "s" : ""}</h2>
                            <button className="crm-modal__close" onClick={() => setBulkConfirmDelete(false)}>×</button>
                        </div>
                        <div className="crm-modal__body">
                            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                                Permanently delete <strong>{selectionCount} lead{selectionCount !== 1 ? "s" : ""}</strong>? <strong>This cannot be undone.</strong>
                            </p>
                        </div>
                        <div className="crm-modal__footer">
                            <button className="admin-btn" onClick={() => setBulkConfirmDelete(false)} disabled={bulkDeleting}>Cancel</button>
                            <button className="admin-btn admin-btn--danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                                {bulkDeleting ? "Deleting…" : `Delete ${selectionCount} Lead${selectionCount !== 1 ? "s" : ""}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InboundLeadList;
